import { v4 as uuidv4 } from 'uuid';

export const POLL_TYPE_SINGLE_CHOICE = 'single_choice';
export const POLL_TYPE_MULTI_SELECT = 'multi_select';
export const SUPPORTED_POLL_TYPES = [POLL_TYPE_SINGLE_CHOICE, POLL_TYPE_MULTI_SELECT];

export const MIN_POLL_OPTIONS = 2;
export const MAX_POLL_OPTIONS = 10;
export const MAX_POLL_TITLE_LENGTH = 250;
export const MAX_POLL_OPTION_LENGTH = 250;

export const createPollOption = (overrides = {}) => ({
  localId: uuidv4(),
  text: '',
  isEditing: true,
  error: null,
  ...overrides,
});

export const createInitialPollState = () => ({
  title: '',
  titleError: null,
  description: '',
  pollType: POLL_TYPE_SINGLE_CHOICE,
  options: [createPollOption(), createPollOption()],
  formError: null,
  formNotice: null,
  clientRequestId: uuidv4(),
});

export const hasMeaningfulPollChanges = pollState => {
  if (!pollState) return false;

  return (
    Boolean(pollState.title?.trim()) ||
    Boolean(pollState.description?.trim()) ||
    pollState.pollType !== POLL_TYPE_SINGLE_CHOICE ||
    pollState.options?.length !== MIN_POLL_OPTIONS ||
    pollState.options?.some(option => Boolean(option?.text?.trim()))
  );
};

export const validatePollOptionText = (text, options = [], currentLocalId = null) => {
  const trimmedText = String(text || '').trim();

  if (!trimmedText) {
    return 'Option cannot be empty.';
  }

  if (trimmedText.length > MAX_POLL_OPTION_LENGTH) {
    return `Option must be ${MAX_POLL_OPTION_LENGTH} characters or fewer.`;
  }

  const normalizedText = trimmedText.toLowerCase();
  const isDuplicate = options.some(option => {
    if (currentLocalId && option.localId === currentLocalId) return false;
    return String(option?.text || '').trim().toLowerCase() === normalizedText;
  });

  if (isDuplicate) {
    return 'Options must be unique.';
  }

  return null;
};

export const validatePollState = pollState => {
  const title = String(pollState?.title || '').trim();
  const pollType = pollState?.pollType;
  const options = Array.isArray(pollState?.options) ? pollState.options : [];
  const nextOptions = options.map(option => ({ ...option, error: null }));
  let titleError = null;
  let formError = null;

  if (title.length > MAX_POLL_TITLE_LENGTH) {
    titleError = `Title must be ${MAX_POLL_TITLE_LENGTH} characters or fewer.`;
  }

  if (!SUPPORTED_POLL_TYPES.includes(pollType)) {
    formError = 'Choose a valid poll type.';
  }

  if (options.length < MIN_POLL_OPTIONS) {
    formError = `Add at least ${MIN_POLL_OPTIONS} options.`;
  }

  if (options.length > MAX_POLL_OPTIONS) {
    formError = `Polls can have at most ${MAX_POLL_OPTIONS} options.`;
  }

  const seenOptionTexts = new Map();
  let validOptionCount = 0;
  nextOptions.forEach(option => {
    const trimmedText = String(option?.text || '').trim();
    const normalizedText = trimmedText.toLowerCase();

    if (!trimmedText) {
      option.error = 'Option cannot be empty.';
      return;
    }

    if (trimmedText.length > MAX_POLL_OPTION_LENGTH) {
      option.error = `Option must be ${MAX_POLL_OPTION_LENGTH} characters or fewer.`;
      return;
    }

    if (seenOptionTexts.has(normalizedText)) {
      option.error = 'Options must be unique.';
      const previousIndex = seenOptionTexts.get(normalizedText);
      if (nextOptions[previousIndex] && !nextOptions[previousIndex].error) {
        nextOptions[previousIndex].error = 'Options must be unique.';
      }
      return;
    }

    seenOptionTexts.set(normalizedText, nextOptions.indexOf(option));
    validOptionCount += 1;
  });

  if (validOptionCount < MIN_POLL_OPTIONS && !formError) {
    formError = `Add at least ${MIN_POLL_OPTIONS} valid options.`;
  }

  const hasErrors = Boolean(titleError || formError || nextOptions.some(option => option.error));

  return {
    isValid: !hasErrors,
    titleError,
    formError,
    options: nextOptions,
  };
};

export const normalisePollPayload = pollState => {
  const validation = validatePollState(pollState);

  if (!validation.isValid) {
    return { validation };
  }

  return {
    validation,
    payload: {
      title: String(pollState.title || '').trim(),
      description: String(pollState.description || '').trim(),
      pollType: pollState.pollType,
      options: pollState.options.map(option => ({
        text: String(option.text || '').trim(),
      })),
    },
  };
};
